/**
 * IndexedDB storage for episodes downloaded into the app.
 * Each HLS segment is its own Blob record, so a download never holds a whole episode in memory
 * and playback reads one segment at a time.
 */

export interface OfflineEpisode {
  id: string; // `${animeId}_${audio}_${number}`
  animeId: string;
  animeTitle: string;
  number: number;
  audio: 'ซับไทย' | 'พากย์ไทย';
  title: string;
  sourceUrl: string;
  durations: number[]; // seconds per segment (rebuilds the playlist)
  segmentCount: number;
  savedSegments: number; // < segmentCount while downloading / after an interrupted download
  sizeBytes: number;
  downloadedAt: string;
  /** Which player of the episode page it came from, e.g. "ตัวเล่น 1 · RUKA" (older downloads have none) */
  source?: string;
}

const DB_NAME = 'AnimeOfflineDB_v1';
const DB_VERSION = 1;
const EPISODES = 'episodes';
const SEGMENTS = 'segments';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('เบราว์เซอร์นี้ไม่รองรับการเก็บวิดีโอในแอป'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(EPISODES)) {
        db.createObjectStore(EPISODES, { keyPath: 'id' }).createIndex('by_anime', 'animeId');
      }
      if (!db.objectStoreNames.contains(SEGMENTS)) db.createObjectStore(SEGMENTS);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('เปิดคลังวิดีโอในแอปไม่ได้'));
  });
  dbPromise.catch(() => (dbPromise = null));
  return dbPromise;
}

function run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const req = fn(tx.objectStore(store));
        tx.oncomplete = () => resolve(req.result);
        tx.onerror = () => reject(tx.error || req.error);
        tx.onabort = () =>
          reject(
            tx.error?.name === 'QuotaExceededError'
              ? new Error('พื้นที่เก็บข้อมูลในเครื่องเต็ม ลบตอนเก่าออกก่อนแล้วลองใหม่')
              : tx.error || new Error('บันทึกไม่สำเร็จ')
          );
      })
  );
}

export const offlineEpisodeId = (animeId: string, audio: string, number: number) =>
  `${animeId}_${audio}_${number}`;

const segKey = (episodeId: string, index: number) => `${episodeId}:${String(index).padStart(5, '0')}`;

export function saveEpisodeMeta(ep: OfflineEpisode): Promise<IDBValidKey> {
  return run(EPISODES, 'readwrite', (s) => s.put(ep));
}

export function saveSegment(episodeId: string, index: number, data: Blob): Promise<IDBValidKey> {
  return run(SEGMENTS, 'readwrite', (s) => s.put(data, segKey(episodeId, index)));
}

export function getSegment(episodeId: string, index: number): Promise<Blob | undefined> {
  return run<Blob | undefined>(SEGMENTS, 'readonly', (s) => s.get(segKey(episodeId, index)));
}

export function getOfflineEpisode(id: string): Promise<OfflineEpisode | undefined> {
  return run<OfflineEpisode | undefined>(EPISODES, 'readonly', (s) => s.get(id));
}

export function getAllOfflineEpisodes(): Promise<OfflineEpisode[]> {
  return run<OfflineEpisode[]>(EPISODES, 'readonly', (s) => s.getAll()).then((list) =>
    list.sort((a, b) => a.animeTitle.localeCompare(b.animeTitle) || a.number - b.number)
  );
}

export function getOfflineEpisodesForAnime(animeId: string): Promise<OfflineEpisode[]> {
  return run<OfflineEpisode[]>(EPISODES, 'readonly', (s) => s.index('by_anime').getAll(animeId)).then(
    (list) => list.sort((a, b) => a.number - b.number)
  );
}

export async function deleteOfflineEpisode(id: string): Promise<void> {
  clearResumePosition(id);
  const range = IDBKeyRange.bound(`${id}:`, `${id}:￿`);
  await run(SEGMENTS, 'readwrite', (s) => s.delete(range));
  await run(EPISODES, 'readwrite', (s) => s.delete(id));
}

// Where the viewer stopped in each episode, so reopening it carries on from there.
// Kept in localStorage: a per-device convenience, losing it only means starting from 0:00.
const resumeKey = (episodeId: string) => `offlinePlayer.resume.${episodeId}`;

export function getResumePosition(episodeId: string): number {
  try {
    const v = Number(localStorage.getItem(resumeKey(episodeId)));
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}

export function setResumePosition(episodeId: string, seconds: number): void {
  try {
    localStorage.setItem(resumeKey(episodeId), String(Math.floor(seconds)));
  } catch {}
}

export function clearResumePosition(episodeId: string): void {
  try {
    localStorage.removeItem(resumeKey(episodeId));
  } catch {}
}

/** Ask the browser not to evict downloads when space runs low (best effort) */
export async function requestPersistentStorage(): Promise<void> {
  try {
    if (navigator.storage?.persist && !(await navigator.storage.persisted())) {
      await navigator.storage.persist();
    }
  } catch {}
}

export async function getStorageEstimate(): Promise<{ usage: number; quota: number } | null> {
  try {
    const e = await navigator.storage?.estimate?.();
    return e ? { usage: e.usage || 0, quota: e.quota || 0 } : null;
  } catch {
    return null;
  }
}
